import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { StrategyStatus } from '@mnc/contracts';
import { cn } from '../../components/ui';
import { STATUS_FILL } from './shared';

/** `fit` is the furthest zoom-out: the whole window scaled to the available width, no sideways scrolling. */
export type Zoom = 'month' | 'quarter' | 'year' | 'fit';
export const ZOOMS: Zoom[] = ['month', 'quarter', 'year', 'fit'];
type Grain = Exclude<Zoom, 'fit'>;

export interface TimelineRow {
  id: string;
  parentId: string | null;
  label: string;
  code?: string;
  badge?: ReactNode;
  start: string | null;
  end: string | null;
  status: StrategyStatus;
  progress: number | null;
  onClick?: () => void;
}

const UNIT_WIDTH: Record<Grain, number> = { month: 96, quarter: 132, year: 168 };
const GRAINS: Grain[] = ['month', 'quarter', 'year'];
/** Narrowest column that still holds its label when fitting. */
const FIT_MIN_UNIT: Record<Grain, number> = { month: 52, quarter: 60, year: 44 };
/** Names column: a third of the panel within these bounds unless the reader dragged the divider (kept per device, like the menu rail). */
const LABEL_AUTO = { min: 280, max: 460, share: 0.34 };
const LABEL_MIN = 200;
/** Bars always keep at least this much room when the divider is dragged. */
const BARS_MIN = 240;
const LABEL_KEY = 'mnc.timeline.label';
const readLabel = (): number | null => {
  try {
    const v = Number(localStorage.getItem(LABEL_KEY));
    return Number.isFinite(v) && v >= LABEL_MIN ? v : null;
  } catch {
    return null;
  }
};
const DAY = 86_400_000;
const parse = (iso: string) => Date.parse(`${iso}T00:00:00Z`);

/** Calendar columns of one grain covering the range (the first column is aligned to the grain). */
function unitsOf(range: { start: number; end: number }, grain: Grain, language: string): Array<{ start: number; end: number; label: string }> {
  const out: Array<{ start: number; end: number; label: string }> = [];
  const d = new Date(range.start);
  const step = grain === 'month' ? 1 : grain === 'quarter' ? 3 : 12;
  let cursor = grain === 'year' ? Date.UTC(d.getUTCFullYear(), 0, 1) : grain === 'quarter' ? Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1) : range.start;
  while (cursor < range.end) {
    const c = new Date(cursor);
    const next = Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + step, 1);
    const label = grain === 'year' ? String(c.getUTCFullYear()) : grain === 'quarter' ? `Q${Math.floor(c.getUTCMonth() / 3) + 1} ${c.getUTCFullYear()}` : c.toLocaleDateString(language, { month: 'short', year: '2-digit', timeZone: 'UTC' });
    out.push({ start: cursor, end: next, label });
    cursor = next;
  }
  return out;
}

/**
 * Gantt-style timeline (MRLCO schedule, generalised): rows nest through `parentId`, groups collapse into one
 * aggregate bar, bars carry the status colour with the progress share filled solid, a dashed red line marks today.
 * The drawing area is always left-to-right; labels keep the page direction.
 */
export function Timeline({ rows, zoom, today, defaultCollapsedDepth = 1, expandSignal }: { rows: TimelineRow[]; zoom: Zoom; today: string; defaultCollapsedDepth?: number; expandSignal?: { all: boolean; n: number } }) {
  const { t, i18n } = useTranslation();
  const [manual, setManual] = useState<{ n: number; collapsed: Set<string> } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState(0);
  const [chosen, setChosen] = useState<number | null>(readLabel);
  const drag = useRef<{ x: number; width: number } | null>(null);
  const empty = rows.length === 0;

  // Panel width, tracked so the names column and `fit` follow the window, the side menu and the panel it sits in.
  // Re-attached when the first rows arrive: the empty state renders no wrapper.
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setBox(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [empty]);

  const clampLabel = (w: number) => Math.round(Math.min(Math.max(LABEL_MIN, box - BARS_MIN), Math.max(LABEL_MIN, w)));
  const labelWidth = box === 0 ? (chosen ?? LABEL_AUTO.min) : chosen !== null ? clampLabel(chosen) : Math.round(Math.min(LABEL_AUTO.max, Math.max(LABEL_AUTO.min, box * LABEL_AUTO.share)));
  const space = Math.max(0, box - labelWidth);

  const saveLabel = (w: number | null) => {
    setChosen(w);
    try {
      if (w === null) localStorage.removeItem(LABEL_KEY);
      else localStorage.setItem(LABEL_KEY, String(w));
    } catch {
      // Storage blocked: the width simply lasts for this visit.
    }
  };
  // The drawing area is always left-to-right, so dragging right always widens the names column.
  const onResizeStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, width: labelWidth };
  };
  const onResizeMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current) setChosen(clampLabel(drag.current.width + e.clientX - drag.current.x));
  };
  const onResizeEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    saveLabel(clampLabel(drag.current.width + e.clientX - drag.current.x));
    drag.current = null;
  };

  const children = useMemo(() => {
    const map = new Map<string | null, TimelineRow[]>();
    const known = new Set(rows.map((r) => r.id));
    for (const r of rows) {
      const key = r.parentId && known.has(r.parentId) ? r.parentId : null;
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return map;
  }, [rows]);

  const depthOf = useMemo(() => {
    const d = new Map<string, number>();
    const walk = (parent: string | null, depth: number) => {
      for (const r of children.get(parent) ?? []) {
        d.set(r.id, depth);
        walk(r.id, depth + 1);
      }
    };
    walk(null, 0);
    return d;
  }, [children]);

  const collapsed = useMemo(() => {
    // A chevron click holds until the next expand/collapse-all; before any signal both sides count as 0.
    if (manual && manual.n === (expandSignal?.n ?? 0)) return manual.collapsed;
    if (expandSignal) return expandSignal.all ? new Set<string>() : new Set(rows.filter((r) => (children.get(r.id) ?? []).length > 0).map((r) => r.id));
    return new Set(rows.filter((r) => (children.get(r.id) ?? []).length > 0 && (depthOf.get(r.id) ?? 0) >= defaultCollapsedDepth).map((r) => r.id));
  }, [manual, expandSignal, rows, children, depthOf, defaultCollapsedDepth]);
  const toggle = (id: string) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setManual({ n: expandSignal?.n ?? 0, collapsed: next });
  };

  /** Aggregate window of a row and everything beneath it. */
  const windowOf = useMemo(() => {
    const cache = new Map<string, { start: number | null; end: number | null }>();
    const calc = (r: TimelineRow): { start: number | null; end: number | null } => {
      if (cache.has(r.id)) return cache.get(r.id)!;
      let start = r.start ? parse(r.start) : null;
      let end = r.end ? parse(r.end) : null;
      for (const c of children.get(r.id) ?? []) {
        const w = calc(c);
        if (w.start !== null && (start === null || w.start < start)) start = w.start;
        if (w.end !== null && (end === null || w.end > end)) end = w.end;
      }
      const w = { start, end };
      cache.set(r.id, w);
      return w;
    };
    return calc;
  }, [children]);

  const range = useMemo(() => {
    const todayMs = parse(today);
    let min = todayMs;
    let max = todayMs;
    for (const r of rows) {
      if (r.start) min = Math.min(min, parse(r.start));
      if (r.end) max = Math.max(max, parse(r.end));
    }
    const s = new Date(min);
    const e = new Date(max);
    const start = Date.UTC(s.getUTCFullYear(), s.getUTCMonth() - 1, 1);
    const end = Date.UTC(e.getUTCFullYear(), e.getUTCMonth() + 2, 1);
    return { start, end };
  }, [rows, today]);

  /** `fit` takes the finest grain whose columns still fit the space, then stretches them to fill it exactly. */
  const { units, unitWidth } = useMemo(() => {
    if (zoom !== 'fit') return { units: unitsOf(range, zoom, i18n.language), unitWidth: UNIT_WIDTH[zoom] };
    for (const grain of GRAINS) {
      const candidate = unitsOf(range, grain, i18n.language);
      if (grain === 'year' || candidate.length * FIT_MIN_UNIT[grain] <= space) return { units: candidate, unitWidth: Math.max(FIT_MIN_UNIT[grain], space / candidate.length) };
    }
    return { units: [], unitWidth: 0 };
  }, [range, zoom, space, i18n.language]);

  const first = units[0]?.start ?? range.start;
  const last = units[units.length - 1]?.end ?? range.end;
  const width = Math.floor(units.length * unitWidth);
  const x = (ms: number) => ((ms - first) / (last - first)) * width;
  const todayX = x(parse(today));

  const visible: Array<{ row: TimelineRow; depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    for (const r of children.get(parent) ?? []) {
      visible.push({ row: r, depth });
      if (!collapsed.has(r.id)) walk(r.id, depth + 1);
    }
  };
  walk(null, 0);

  if (rows.length === 0) return <p className="p-6 text-center text-sm text-neutral-500">{t('strategy.schedule.noRows')}</p>;

  return (
    <div ref={wrap} className="overflow-x-auto">
      <div dir="ltr" className="relative min-w-full" style={{ width: labelWidth + width }}>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('strategy.schedule.resizeNames')}
          title={t('strategy.schedule.resizeNames')}
          tabIndex={0}
          className="group/resize no-print absolute inset-y-0 z-20 w-2 -translate-x-1/2 cursor-col-resize touch-none select-none focus-visible:outline-2 focus-visible:outline-brand-500"
          style={{ left: labelWidth }}
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          onDoubleClick={() => saveLabel(null)}
          onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); saveLabel(clampLabel(labelWidth + (e.key === 'ArrowRight' ? 16 : -16))); } }}
        >
          <div className="mx-auto h-full w-px bg-neutral-200 group-hover/resize:w-0.5 group-hover/resize:bg-brand-500 dark:bg-neutral-800" />
        </div>
        <div className="sticky top-0 z-10 flex border-b border-neutral-200 bg-neutral-50 text-[11px] text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="shrink-0 px-3 py-1.5 font-medium" style={{ width: labelWidth }}>{t('strategy.schedule.item')}</div>
          <div className="relative" style={{ width }}>
            {units.map((u) => <span key={u.start} className="absolute top-0 overflow-hidden whitespace-nowrap border-s border-neutral-200 px-1.5 py-1.5 dark:border-neutral-800" style={{ left: x(u.start), width: x(u.end) - x(u.start) }}>{u.label}</span>)}
            <div className="h-7" />
          </div>
        </div>
        <ul>
          {visible.map(({ row, depth }) => {
            const kids = children.get(row.id) ?? [];
            const isGroup = kids.length > 0;
            const isCollapsed = collapsed.has(row.id);
            const w = isGroup && isCollapsed ? windowOf(row) : { start: row.start ? parse(row.start) : null, end: row.end ? parse(row.end) : null };
            const hasBar = w.start !== null && w.end !== null;
            const left = hasBar ? x(w.start!) : 0;
            const right = hasBar ? x(w.end! + DAY) : 0;
            return (
              <li key={row.id} className={cn('flex border-b border-neutral-100 text-sm dark:border-neutral-800', isGroup ? 'bg-neutral-50/60 dark:bg-neutral-900/40' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/40')}>
                <div className="flex shrink-0 items-center gap-1 px-2 py-1.5" style={{ width: labelWidth, paddingInlineStart: `${depth * 14 + 8}px` }}>
                  <button type="button" className={cn('shrink-0 rounded p-0.5 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800 dark:hover:bg-neutral-700 dark:hover:text-neutral-100', !isGroup && 'invisible')} onClick={() => toggle(row.id)} aria-expanded={!isCollapsed} aria-label={isCollapsed ? t('app.expand') : t('app.collapse')} title={isCollapsed ? t('app.expand') : t('app.collapse')}>
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <button type="button" className={cn('flex min-w-0 flex-1 items-center gap-1.5 text-start', row.onClick && 'hover:underline')} onClick={row.onClick} disabled={!row.onClick}>
                    {row.badge}
                    {row.code && <code className="shrink-0 text-[11px] text-neutral-500">{row.code}</code>}
                    <span className={cn('line-clamp-2 min-w-0 leading-snug wrap-break-word', isGroup && 'font-medium')} dir="auto" title={row.label}>{row.label}</span>
                  </button>
                </div>
                <div className="relative" style={{ width }}>
                  <div className="pointer-events-none absolute inset-y-0 border-s border-dashed border-red-400" style={{ left: todayX }} />
                  {hasBar ? (
                    <div className={cn('absolute top-1/2 h-3 -translate-y-1/2 overflow-hidden rounded-full', isGroup ? 'opacity-90' : '')} style={{ left, width: Math.max(6, right - left) }} title={`${row.start ?? ''} → ${row.end ?? ''}`}>
                      <div className={cn('absolute inset-0 opacity-30', STATUS_FILL[row.status])} />
                      <div className={cn('absolute inset-y-0 start-0', STATUS_FILL[row.status])} style={{ width: `${row.progress ?? (row.status === 'completed' ? 100 : 0)}%` }} />
                    </div>
                  ) : (
                    <span className="absolute top-1/2 -translate-y-1/2 px-2 text-[11px] text-neutral-400" style={{ left: Math.max(0, todayX + 6) }}>{t('strategy.schedule.noDatesRow')}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function TimelineLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-neutral-500">
      {(['planned', 'in_progress', 'at_risk', 'delayed', 'completed'] as StrategyStatus[]).map((s) => (
        <span key={s} className="inline-flex items-center gap-1"><span className={cn('h-2 w-3 rounded-full', STATUS_FILL[s])} />{t(`strategy.statuses.${s}`)}</span>
      ))}
      <span className="inline-flex items-center gap-1"><span className="h-3 border-s border-dashed border-red-400" />{t('strategy.schedule.today')}</span>
    </div>
  );
}
